<template>
  <section
    ref="dropArea"
    class="drop-area"
  >
    <form class="drop-area__form">
      <input
        :id="systemFilesUid"
        ref="systemFiles"
        type="file"
        multiple
        accept="image/*"
        @change="previewSystemFiles"
      >
      <label
        class="drop-area__form-label"
        :for="systemFilesUid"
      >
        <h6 v-if="title">{{ title }}</h6>
        <p>Drop Images here or <span>browse </span></p>
      </label>

      <ElPopover
        v-model="showAssetManager"
        width="300"
        placement="bottom"
        popper-class="drop-area__designhub-menu"
      >
        <StitchAssetBrowser
          v-if="showAssetManager"
          v-click-outside="closeAssetManager"
          :asset-type="canUserAccessFeatureStyleIntegration ? null : 'job'"
          @select-files="handleSelectedImages"
        />
        <ElButton
          slot="reference"
          ref="designHubMenuTrigger"
          class="drop-area__designhub-menu-button"
          size="mini"
          plain
        >
          <div class="drop-area__designhub-menu-button-content">
            <Icon
              icon="plm-styles"
              size="24"
              fill="#5856d6"
            />
            DesignHUB
          </div>
        </ElButton>
      </ElPopover>
    </form>
  </section>
</template>

<script>
import features from '@/constants/features'
import ClickOutside from 'element-ui/src/utils/clickoutside'
import { Workspace } from '@/mixins/workspace'
import Icon from '@/components/Icon.vue'

export default {
  name: 'DropArea',

  components: {
    Icon
  },

  directives: { ClickOutside },

  mixins: [Workspace],

  props: {
    title: {
      type: String,
      default: ''
    }
  },

  data: () => ({
    systemFilesUid: null,
    showAssetManager: false,
    selectedAssetType: null
  }),

  computed: {
    /**
     * @returns {boolean}
     */
    canUserAccessFeatureStyleIntegration () {
      return this.$featureFlagsManager.canAccessFeature(
        features.STYLE_INTEGRATION,
        {
          user: this.user
        }
      )
    }
  },

  mounted () {
    this.systemFilesUid = `systemFiles${this._uid}`

    const dropArea = this.$refs.dropArea

    dropArea.addEventListener('dragenter', this.dragEnter, false)
    dropArea.addEventListener('dragleave', this.dragLeave, false)
    dropArea.addEventListener('dragover', this.dragOver, false)
    dropArea.addEventListener('drop', this.drop, false)
  },

  beforeDestroy () {
    const dropArea = this.$refs.dropArea

    dropArea.removeEventListener('dragenter', this.dragEnter, false)
    dropArea.removeEventListener('dragleave', this.dragLeave, false)
    dropArea.removeEventListener('dragover', this.dragOver, false)
    dropArea.removeEventListener('drop', this.drop, false)
  },

  methods: {
    /**
     * When Is It Fired?
     * The dragged item is dragged over dropArea,
     * making it the target for the drop event if the user drops it there.
     *
     * @param {Event} event
     */
    dragEnter (event) {
      this.preventDefaults(event)
      this.addHighlightClass(event.currentTarget)
    },

    /**
     * When Is It Fired?
     * The dragged item is dragged off of dropArea and onto another element,
     * making it the target for the drop event instead.
     *
     * @param {Event} event
     */
    dragLeave (event) {
      this.preventDefaults(event)
      this.removeHighlightClass(event.currentTarget)
    },

    /**
     * When Is It Fired?
     * Every few hundred milliseconds,
     * while the dragged item is over dropArea and is moving.
     *
     * @param {Event} event
     */
    dragOver (event) {
      this.preventDefaults(event)
      this.addHighlightClass(event.currentTarget)
    },

    /**
     * When Is It Fired?
     * The user releases their mouse button,
     * dropping the dragged item onto dropArea.
     *
     * @param {Event} event
     */
    drop (event) {
      const data = event.dataTransfer
      const files = data.files

      this.preventDefaults(event)
      this.removeHighlightClass(event.currentTarget)
      this.handleFiles(files)
    },

    /**
     * @param {Event} event
     */
    preventDefaults (event) {
      event.preventDefault()
      event.stopPropagation()
    },

    /**
     * @param {Element} element
     */
    addHighlightClass (element) {
      element.classList.add('drop-area--highlight')
    },

    /**
     * @param {Element} element
     */
    removeHighlightClass (element) {
      element.classList.remove('drop-area--highlight')
    },

    /**
     * @param {object} files
     */
    handleFiles (files) {
      files = [...files]

      this.$emit('handle-files', files)
    },

    /**
     */
    previewSystemFiles () {
      if (this.$refs.systemFiles) {
        const files = [...this.$refs.systemFiles.files]

        const validFiles = files.filter(file => {
          return file.type && file.type.includes('image')
        })

        this.handleFiles(validFiles)

        this.$refs.systemFiles.value = ''
      }
    },

    /**
     * @param {string[]} imageUrls
     */
    handleSelectedImages (imageUrls) {
      this.handleFiles(imageUrls)

      this.closeAssetManager()
    },

    /**
     */
    closeAssetManager () {
      this.showAssetManager = false
    }
  }
}
</script>

<style lang="scss" scoped>
$height-dropzone: $spacer-unit-x8;
$z-index-button-designhub-menu: 1;
$border-radius-half-default: $border-radius-default / 2;

.drop-area {
  position: relative;
  margin: $spacer-unit-x2;
  padding: $spacer-unit-x2;
  background-color: $purple-light;
  border: $border-size * 2 dashed $purple-opacity-70;
  border-radius: $border-radius-default;
  cursor: pointer;

  &.drop-area--highlight {
    background-color: $purple-light-hover;
  }

  &:hover {
    background-color: $purple-light-hover;
  }
}

.drop-area__form {
  @include size(100%, $height-dropzone);
  @include flex-horizontal-space-between;

  border-radius: $border-radius-half-default;

  input {
    @include size(inherit);

    display: none;
    cursor: pointer;
    opacity: 0;
  }
}

.drop-area__form-label {
  @include size(inherit);

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  color: $purple;
  cursor: pointer;

  h6 {
    display: block;
    color: $purple;
    font-size: $font-size-larger;
    text-transform: uppercase;
  }

  p {
    margin: 0;
    margin-top: spacing(0.5);
    font-weight: $font-weight-regular;
    font-size: $font-size-medium;

    span {
      text-decoration: underline;
    }
  }
}

.drop-area__designhub-menu-button {
  z-index: $z-index-button-designhub-menu;
  color: $purple;
  background-color: transparent;
  border-color: $purple;
  cursor: pointer;

  &:focus,
  &:hover {
    color: $purple;
    background-color: $purple-opacity-40;
    border-color: $purple;
  }

  .drop-area__designhub-menu-button-content {
    @include flex-horizontal-space-between;

    pointer-events: none;
  }

  .drop-area__designhub-menu-button-content * {
    pointer-events: none;
  }
}
</style>

<style lang="scss">
.drop-area__designhub-menu {
  padding: 0;
}
</style>
